
import React, { useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { where } from "firebase/firestore";
import { WaitlistForm } from "./waitlist/WaitlistForm";
import { SuccessMessage } from "./waitlist/SuccessMessage";

// ***** Firebase Imports *****
import { collection, addDoc, getDocs, query, orderBy, getFirestore, serverTimestamp } from "firebase/firestore";
import app from "../../firebase/firebaseConfig"; // <-- Your Firebase config file
const firestore = getFirestore(app); // Initialize Firestore

interface WaitlistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableName?: "waitlist" | "notify_me" | "eateries_requests" | "arriving_soon";
}
type TableNames = "waitlist" | "notify_me" | "eateries_requests" | "arriving_soon";

// Using the fields "name" and "type" from the Firestore "Eateries" collection
type Eatery = {
  name: string;
  type: string;
};
const fetchEateries = async () => {
  const eateriesCol = collection(firestore, "Eateries");
  const q = query(
    eateriesCol,
    where("status", "==", "Active")
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => doc.data() as Eatery);
};

// Map legacy table names to the new Firebase collection names
const getCollectionName = (tableNameProp: TableNames | undefined, pathname: string): string => {
  if (tableNameProp) {
    switch (tableNameProp) {
      case "waitlist":
        return "all_issued_waitlist";
      case "notify_me":
        return "sold_out_waitlist";
      case "arriving_soon":
        return "arriving_soon_waitlist";
      case "eateries_requests":
        return "eateries_requests";
      default:
        return tableNameProp;
    }
  }
  if (pathname === "/boost-cards-on-the-way") {
    return "sold_out_waitlist";
  } else if (pathname === "/available-eateries") {
    return "eateries_requests";
  } else if (pathname === "/arriving-soon") {
    return "arriving_soon_waitlist";
  } else {
    return "all_issued_waitlist";
  }
};
export function WaitlistDialog({
  open,
  onOpenChange,
  tableName
}: WaitlistDialogProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedEatery, setSelectedEatery] = useState("");
  const [preference, setPreference] = useState("physical");
  const [contactMethod, setContactMethod] = useState("phone");
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();
  const location = useLocation();
  
  // Determine if this is from "Pre-Order" button or general eatery request
  const isFromPreOrder = tableName === "waitlist" && location.pathname === "/available-eateries";
  const isRequestEatery = tableName === "eateries_requests" || location.pathname === "/available-eateries";
  
  // Check if the form is for requesting an eatery (to hide the preference field)
  const isEateryRequest = tableName === "eateries_requests" || 
                          (location.pathname === "/available-eateries" && !isFromPreOrder);
  
  const { data: eateries } = useQuery({
    queryKey: ["eateries-list"],
    queryFn: fetchEateries,
    enabled: open // Only fetch when dialog is open
  });
  
  const resetForm = () => {
    setName("");
    setEmail("");
    setPhoneNumber("");
    setSelectedEatery("");
    setPreference("physical");
    setContactMethod("phone");
    setSubmitted(false);
  };
  
  const handleClose = () => {
    onOpenChange(false);
    // Reset the form after a short delay to prevent seeing the reset during close animation
    setTimeout(resetForm, 300);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
  
    // Manual validation for empty eatery selection
    if (!selectedEatery) {
      toast({
        title: "Missing Selection",
        description: "Please select an eatery before submitting.",
        variant: "destructive"
      });
      return;
    }
  
    const collectionName = getCollectionName(tableName, location.pathname);
    let error = null;
    try {
      await addDoc(collection(firestore, collectionName), {
        name,
        email,
        phone_number: phoneNumber,
        eatery: selectedEatery,
        preference: preference,
        contact_method: contactMethod,
        timestamp: serverTimestamp()
      });
    } catch (err) {
      console.error("Firestore submission error:", err);
      error = err;
    }
  
    if (error) {
      toast({
        title: "Error",
        description: "Failed to submit your request. Please try again.",
        variant: "destructive"
      });
      return;
    }
  
    setSubmitted(true);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[380px] max-w-[90%] w-[90%] bg-white border-2 shadow-xl p-5">
        {!submitted ? (
          <WaitlistForm
            name={name}
            setName={setName}
            email={email}
            setEmail={setEmail}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            selectedEatery={selectedEatery}
            setSelectedEatery={setSelectedEatery}
            preference={preference}
            setPreference={setPreference}
            contactMethod={contactMethod}
            setContactMethod={setContactMethod}
            eateries={eateries}
            isEateryRequest={isEateryRequest}
            isRequestEatery={isRequestEatery}
            isFromPreOrder={isFromPreOrder}
            handleSubmit={handleSubmit}
          />
        ) : (
          <SuccessMessage
            name={name}
            selectedEatery={selectedEatery}
            isEateryRequest={isEateryRequest}
            handleClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
